<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryLocation } from '@/api/inventory';
import { usePermissions } from '@/composables/usePermissions';
import { apiErrorText } from '@/utils/api-errors';

type ManualKind = 'supplier' | 'warehouse';

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canManage = computed(() => hasPermission.value('inventory', 'manage'));

const draft = ref<{ id?: number; kind: ManualKind; name: string } | null>(null);
const showArchived = ref<Record<ManualKind, boolean>>({ supplier: false, warehouse: false });
const error = ref('');

const { data: locations, isPending } = useQuery({
  queryKey: ['inventoryLocations', 'manual'],
  queryFn: () => inventoryApi.locations({ kinds: ['supplier', 'warehouse'] }),
  initialData: [],
});

const { data: archived } = useQuery({
  queryKey: ['inventoryLocations', 'manual', 'discarded'],
  queryFn: () => inventoryApi.locations({ kinds: ['supplier', 'warehouse'], scope: 'discarded' }),
  initialData: [],
  enabled: canManage,
});

const sections = computed(() => [
  {
    kind: 'supplier' as const,
    title: 'Proveedores',
    hint: 'Desde dónde ingresa el stock: proveedores, terceros que entregan insumos o compras propias. ' +
      'Los equipos pueden quedar a nombre de un proveedor (dueño).',
    rows: locations.value.filter((location) => location.kind === 'supplier'),
    archivedRows: archived.value.filter((location) => location.kind === 'supplier'),
  },
  {
    kind: 'warehouse' as const,
    title: 'Bodegas',
    hint: 'Solo si la empresa guarda stock propio. Sin bodegas, el stock vive en el domicilio de cada paciente.',
    rows: locations.value.filter((location) => location.kind === 'warehouse'),
    archivedRows: archived.value.filter((location) => location.kind === 'warehouse'),
  },
]);

function refresh() {
  error.value = '';
  draft.value = null;
  queryClient.invalidateQueries({ queryKey: ['inventoryLocations'] });
}

function fail(failure: unknown) {
  error.value = apiErrorText(failure, 'No se pudo guardar la ubicación.');
}

const save = useMutation({
  mutationFn: () => inventoryApi.saveLocation(draft.value as { kind: ManualKind; name: string }),
  onSuccess: refresh,
  onError: fail,
});

const archive = useMutation({
  mutationFn: (location: InventoryLocation) => inventoryApi.archiveLocation(location.id),
  onSuccess: refresh,
  onError: fail,
});

// Updating an archived location restores it.
const restore = useMutation({
  mutationFn: (location: InventoryLocation) => inventoryApi.saveLocation({ id: location.id, name: location.name }),
  onSuccess: refresh,
  onError: fail,
});
</script>
<template>
  <div class="pa-4 d-flex flex-column ga-6">
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      :text="error"
      closable
      @click:close="error = ''"
    />
    <section
      v-for="section in sections"
      :key="section.kind"
    >
      <div class="mb-1 d-flex align-center">
        <h3 class="text-h6">
          {{ section.title }}
        </h3>
        <v-spacer />
        <v-btn
          v-if="canManage"
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-plus"
          @click="draft = { kind: section.kind, name: '' }"
        >
          Agregar
        </v-btn>
      </div>
      <p class="mb-2 text-body-2 text-medium-emphasis">
        {{ section.hint }}
      </p>
      <v-progress-linear
        v-if="isPending"
        indeterminate
      />
      <v-list
        v-else
        density="compact"
        border
        rounded
      >
        <v-list-item
          v-if="section.rows.length === 0 && draft?.kind !== section.kind"
          class="text-medium-emphasis"
        >
          Sin {{ section.title.toLowerCase() }}
        </v-list-item>
        <v-list-item
          v-for="location in section.rows"
          :key="location.id"
          :title="location.name"
        >
          <template
            v-if="canManage"
            #append
          >
            <v-btn
              icon="mdi-pencil"
              variant="text"
              size="small"
              aria-label="Renombrar"
              @click="draft = { id: location.id, kind: section.kind, name: location.name }"
            />
            <v-btn
              icon="mdi-archive"
              variant="text"
              size="small"
              color="warning"
              aria-label="Archivar"
              @click="archive.mutate(location)"
            />
          </template>
        </v-list-item>
        <div
          v-if="draft?.kind === section.kind"
          class="px-4 pt-4 pb-2"
        >
          <div class="d-flex flex-wrap ga-2 align-center">
            <v-text-field
              v-model="draft.name"
              :label="draft.id ? 'Nuevo nombre' : 'Nombre'"
              density="compact"
              variant="outlined"
              hide-details
              autofocus
              @keyup.enter="draft.name.trim() && save.mutate()"
            />
            <v-btn
              color="primary"
              :disabled="!draft.name.trim()"
              :loading="save.isPending.value"
              @click="save.mutate()"
            >
              Guardar
            </v-btn>
            <v-btn
              variant="text"
              @click="draft = null"
            >
              Cancelar
            </v-btn>
          </div>
        </div>
      </v-list>
      <template v-if="canManage && section.archivedRows.length">
        <v-btn
          variant="text"
          size="small"
          class="mt-1"
          :prepend-icon="showArchived[section.kind] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          @click="showArchived[section.kind] = !showArchived[section.kind]"
        >
          Archivados ({{ section.archivedRows.length }})
        </v-btn>
        <v-list
          v-if="showArchived[section.kind]"
          density="compact"
          border
          rounded
        >
          <v-list-item
            v-for="location in section.archivedRows"
            :key="location.id"
            :title="location.name"
            class="text-medium-emphasis"
          >
            <template #append>
              <v-btn
                size="small"
                variant="text"
                color="primary"
                prepend-icon="mdi-archive-arrow-up"
                :loading="restore.isPending.value && restore.variables.value?.id === location.id"
                @click="restore.mutate(location)"
              >
                Restaurar
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>
    </section>
  </div>
</template>
